import { css } from '@emotion/react';
import {
  faCog, faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { SquareIconButtonComponent } from '~/_shared/baseComponents/buttons';
import { RoundButtonComponent } from '~/_shared/baseComponents/buttons/roundButton/roundButton.component';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { RegularDropdownComponent } from '~/_shared/baseComponents/dropdown';
import { TextSearchBoxComponent } from '~/_shared/components/textSearchBox/textSearchBox.component';
import { CONFIG } from '~/_shared/constants/config';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { getMapSearchPlaceholder } from '~/map/filter/mapFilter.helpers';
import { type SearchItem } from '../search/mapSearch.helpers';
import {
  createDisplayName,
  createFilterByIcon,
  MapSearchFilterBy,
} from './mapSearchFilterBy.enum';

const filterHeight = 48;

const filterShadowStyle = (theme: Theme) => css({
  boxShadow: `0px 2px 4px ${theme.shadowColors.secondary}`,
});

const wrapperStyle = ({ isMobile }: { isMobile: boolean}) => css({
  display: 'flex',
  backgroundColor: 'transparent',
  width: isMobile ? '100%' : undefined,

  '& > * + *': {
    marginLeft: 5,
  },
});

const searchBoxStyle = css({
  height: filterHeight,
});

const buttonStyle = [filterShadowStyle, {
  flexShrink: 0,
}];

const closeButtonStyle = [filterShadowStyle, {
  minWidth: 25,
  width: 25,
  minHeight: 25,
  height: 25,
}];

const dropdownListStyle = ({ isSelected }: { isSelected: boolean }) => (theme: Theme) => [filterShadowStyle, {
  color: isSelected ? theme.textColors.primary : theme.textColors.secondary,
}];

const dropdownRootStyle = [filterShadowStyle, {
  width: '100%',
  minWidth: 110,
  maxWidth: 153,
}];

const dropdownTriggerStyle = (props: { isSelected: boolean }) => [dropdownListStyle(props), {
  height: '100%',
  width: '100%',
}];

type MapFilterProps = Readonly<{
  areSearchSuggestsLoading: boolean;
  className?: string;
  filterBy: MapSearchFilterBy | null;
  isMobileScreen: boolean;
  searchSuggests: SearchItem[];
  searchText: string;

  onCloseSearchBar?: () => void;
  onFilterByChange?: (newFilterBy: MapSearchFilterBy) => void;
  onSearchSuggestSubmit: (suggest: SearchItem) => void;
  onSearchTextChange: (newText: string) => void;
  onShowConfig?: () => void;
}>;

export const MapFilterComponent: FC<MapFilterProps> = props => {
  const [t] = useTranslation();

  const isFilterBySelected = props.filterBy !== null;
  const showFilter = !!props.onFilterByChange;

  const filterByOptions = Object.values(MapSearchFilterBy).map(value => ({
    value,
    name: createDisplayName(value, t),
    icon: createFilterByIcon(value),
  })).filter(option => CONFIG.ENABLE_PLACES || option.value !== MapSearchFilterBy.PlacesAndPoi);

  return (
    <div
      css={wrapperStyle({ isMobile: props.isMobileScreen })}
      className={props.className}
    >
      {props.onFilterByChange && !props.isMobileScreen && (
        <RegularDropdownComponent
          css={dropdownRootStyle}
          onChange={props.onFilterByChange}
          value={props.filterBy}
          options={filterByOptions}
          fallbackName={t('Search By')}
          triggerStyle={dropdownTriggerStyle({ isSelected: isFilterBySelected })}
          listStyle={dropdownListStyle({ isSelected: isFilterBySelected })}
        />
      )}

      <TextSearchBoxComponent
        css={filterShadowStyle}
        inputStyle={searchBoxStyle}
        value={props.searchText}
        onClear={() => props.onSearchTextChange('')}
        onChange={props.onSearchTextChange}
        searchSuggests={props.searchSuggests}
        onSearchSuggestSubmit={props.onSearchSuggestSubmit}
        areSearchSuggestsLoading={props.areSearchSuggestsLoading}
        placeholder={showFilter ? getMapSearchPlaceholder(props.filterBy, t) : t('Search...')}
      />

      {!props.isMobileScreen && props.onShowConfig && (
        <SquareIconButtonComponent
          css={buttonStyle}
          icon={faCog}
          onClick={props.onShowConfig}
          buttonSize={filterHeight}
        />
      )}

      {props.onCloseSearchBar && (
        <RoundButtonComponent
          css={closeButtonStyle}
          icon={faTimes}
          buttonStyle={RoundButtonStyle.Secondary}
          onClick={props.onCloseSearchBar}
        />
      )}
    </div>
  );
};
